import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Quality",
  "lead": "Globally, most teachers are trained but in sub-Saharan Africa, the percentage of trained teachers fell gradually from 85% in 2000 to 69% in 2022."
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Figure, HighlightByIndex, IndicatorReference, QualityFramework, RelatedMaterial, ScrollyArea, ScrollySection, ScrollySticky, Tooltip} = _components;
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!QualityFramework) _missingMdxReference("QualityFramework", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  if (!Tooltip) _missingMdxReference("Tooltip", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_QUALITY"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Good quality education cannot be equated with, or reduced to, learning outcomes. Too many children are denied sufficiently trained teachers, good school infrastructure or a safe, non-violent learning environment."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "It is hard to monitor good quality education because each country understands and defines it in different ways. Apart from learning outcomes, there are not many indicators for measuring quality in the global education goal, SDG 4."
    }), "\n", _jsx(_components.h2, {
      id: "teachers",
      children: "Teachers"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.c.2",
      children: [_jsx(_components.p, {
        children: "Teachers are the cornerstone of quality in education. However, after 2000, in the push to achieve universal primary education, many more students entered the classroom and the recruitment of teachers did not keep pace. There are over-crowded classrooms as a result, and the quality of education is suffering."
      }), _jsx(QualityFramework, {
        highlighted: "teachers",
        description: "Learn more about the framework for education quality"
      }), _jsx(_components.p, {
        children: "The graphs below look at the ratio of pupils to teachers in school. In some sub-Saharan African countries, while the ratio has improved since 2000, the pupil per teacher ratio is still worse than in the 1980s."
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "PTR"
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["In ", _jsx(HighlightByIndex, {
              index: "0",
              children: "sub-Saharan Africa"
            }), ", after the push to make primary school free between 1990 and 2000, children entered classrooms faster than countries could recruit new teachers. Pupil-teacher-ratios increased often fairly dramatically as a result."]
          })
        }), _jsx(ScrollySection, {
          children: _jsxs(_components.p, {
            children: ["In ", _jsx(HighlightByIndex, {
              index: "6",
              children: "Europe and Northen America"
            }), ", meanwhile, the ratio has been constant at about 14 students per teacher since 2005."]
          })
        })]
      })]
    }), "\n", _jsx(_components.p, {
      children: "Teaching is a challenging task and teachers who are not well prepared enter the classroom with a serious deficit. There are two main ways that countries measure how well prepared they are. Teachers are assessed by:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Firstly, whether they are ", _jsx(_components.strong, {
          children: "qualified"
        }), ", which indicates whether they have the expected academic credentials according to national standards or not."]
      }), "\n", _jsxs(_components.li, {
        children: ["Secondly, whether they are ", _jsx(_components.strong, {
          children: "trained"
        }), ", which, regardless of their qualification, assesses if they received appropriate teacher training or not."]
      }), "\n"]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.c.1",
      children: [_jsxs(_components.p, {
        children: ["Being trained means that teachers have received training in ", _jsx(Tooltip, {
          title: "Pedagogical knowledge",
          content: "Knowledge on approaches, methods and techniques of teaching.",
          children: "pedagogical knowledge"
        }), " and ", _jsx(Tooltip, {
          title: "Professional knowledge",
          content: "Knowledge on, for example, the legal frameworks governing the teaching profession.",
          children: "professional knowledge"
        }), ". Some programmes may also cover ", _jsx(Tooltip, {
          title: "Content knowledge",
          content: "Knowledge of the curriculum, subject matter and use of relevant materials.",
          children: "content knowledge"
        }), ". But the type of training required differs by country and relevant information is scarce. Because of these differences, despite being a crucial element of a quality education, the indicator on trained teachers [4.c.1] is one of the least comparable in the SDG 4 monitoring framework."]
      }), _jsxs(_components.p, {
        children: ["Globally, the majority of teachers are qualified, trained, or both. For instance, in ", _jsx(HighlightByIndex, {
          index: "3",
          children: "Latin America and the Caribbean"
        }), ", 80% of teachers are trained."]
      }), _jsxs(_components.p, {
        children: ["By contrast, in ", _jsx(HighlightByIndex, {
          index: "0",
          children: "sub-Saharan Africa"
        }), ", the percentage of trained teachers fell from 85% in 2000 to 69% in 2022."]
      }), _jsx(Figure, {
        id: "TRAINED"
      }), _jsx(_components.p, {
        children: "There is no easy fix for countries that lack qualified and trained teachers. Many do not have enough eligible teachers to meet the demand given the low number of tertiary graduates overall. This goes to show how inter-connected problems within an education system can become: problems in one level, such as low secondary school completion, can easily result in repercussions elsewhere, such as limited trained teachers for students later on."
      })]
    }), "\n", _jsx(_components.h2, {
      id: "infrastructure",
      children: "Infrastructure"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1e",
      children: [_jsxs(_components.p, {
        children: ["An appropriate learning environment must have adequate ", _jsx(_components.strong, {
          children: "water"
        }), " and sanitation facilities, especially for girls. Yet, basic water was available in only 55% of primary schools in sub-Saharan Africa, 88% in Eastern and South-Eastern Asia, and 85% in Central and Southern Asia."]
      }), _jsx(QualityFramework, {
        highlighted: "material",
        description: "Learn more about the framework for education quality"
      })]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1f",
      children: [_jsxs(_components.p, {
        children: ["In particular, ", _jsx(_components.strong, {
          children: "sanitation"
        }), " facilities that lack privacy may not be considered safe, while facilities where menstrual hygiene cannot be practiced can prevent girls from attending school. Having single-sex sanitation facilities is therefore a crucial policy move to prevent widespread late enrolment at primary school and female drop out from school over time."]
      }), _jsx(_components.p, {
        children: "The graph below shows that fewer than half of primary schools have single-sex sanitation facilities not only in sub-Saharan African countries but also in Asian countries such as Afghanistan, the Lao PDR and Nepal. In Indonesia, only 64% of primary, 76% of lower secondary and 78% of upper secondary schools had access to single-sex sanitation facilities."
      }), _jsx(Figure, {
        id: "SANITATION"
      })]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1a",
      children: [_jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Electricity"
        }), " is a basic need for a quality learning environment and yet many countries are without it. In low income countries, 72% of primary and 57% of upper secondary schools do not have electricity."]
      }), _jsx(_components.p, {
        children: "As the graph below shows, fewer than half of primary schools have electricity in Nicaragua and Pakistan. In some sub-Saharan African countries, access to electricity is extremely rare in schools: 12% of schools have electricity in Madagascar and just 1% in Chad. Without electricity, light may be inadequate, and teachers cannot use computers for administration or training. A lack of electricity means there will be no ventilation, cooling or heating, which may render classrooms unsuitable for teaching and learning."
      })]
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.1b",
      children: [_jsxs(_components.p, {
        children: ["A lack of electricity also means that using technology and the ", _jsx(_components.strong, {
          children: "internet"
        }), " is impossible. However, electricity is not the only factor slowing down the spread of the internet. In middle income countries, 36% of primary schools and 20% of upper secondary schools had electricity but no internet. Even if schools are connected to a power source, power surges and brownouts are common. Internet connectivity depends on the national telecommunications infrastructure, geography and schools’ ability to pay for the service."]
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "ELECTRIC"
          })
        }), _jsx(ScrollySection, {
          id: "LKA",
          children: _jsxs(_components.p, {
            children: ["In ", _jsx(_components.strong, {
              children: "Sri Lanka"
            }), ", while all primary schools have electricity, only 41% have access to the internet. The same is true for 42% of schools in Turkmenistan."]
          })
        }), _jsx(ScrollySection, {
          id: "ECU,URY",
          children: _jsxs(_components.p, {
            children: ["Many Latin American countries have schools with no electricity and no internet. Only around 60% of schools in ", _jsx(_components.strong, {
              children: "Ecuador"
            }), " and 65% in Argentina can say they have access to both. One exception is ", _jsx(_components.strong, {
              children: "Uruguay"
            }), ", where 100% of primary schools have access to both."]
          })
        })]
      })]
    }), "\n", _jsx(_components.h2, {
      id: "violence",
      children: "Violence"
    }), "\n", _jsx(_components.p, {
      children: "School-related violent acts or threats can be psychological, physical or sexual and occur on school premises but also on the way to school, at home or in cyberspace. They often come about because of unequal power dynamics and are often the result of negative gender norms and stereotypes."
    }), "\n", _jsx(_components.p, {
      children: "While attention usually focuses on extreme events, such as shootings, it is the more common and often unnoticed forms of violence such as bullying that have the largest negative impact on children and adolescents' education. Data on these issues remains insufficient because the questions that are asked when collecting data involve different definitions of violent behaviour, different time scales for reporting, incomparable response options, privacy arrangements or ethical protocols. This is in addition to under-reporting also taking place for forms of violence such as homophobic bullying, which involve taboos."
    }), "\n", _jsx(QualityFramework, {
      highlighted: "school",
      description: "Learn more about the framework for education quality"
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.a.2",
      children: [_jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Bullying"
        }), " is the most widely documented form of violence in schools. It can include physical violence, verbal abuse and the intent to cause psychological harm through humiliation or exclusion. The figure below shows that at least one in three adolescents, frequently more boys than girls, are victims of bullying in most countries."]
      }), _jsx(Figure, {
        id: "BULLY_FIGHT"
      })]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Physical violence"
      }), " includes being involved in physical fights at school, carrying or being threatened by a weapon such as a gun, knife or club on school property, being a gang member or associate, or suffering physical violence in the hands of a staff member."]
    }), "\n", _jsx(_components.p, {
      children: "At least 10% of 15-year-old boys in 24 countries reported having been involved in physical fights 3 times or more in 2022, and the prevalence reached over 30% in Armenia."
    }), "\n", _jsx(_components.h2, {
      id: "conclusion",
      children: "Conclusion"
    }), "\n", _jsx(_components.p, {
      children: "Internationally comparable data on teacher indicators is surprisingly scarce at the same time. Relatively few countries report even a basic headcount of teachers, and that does not take teaching hours, teachers in administrative positions and other complexities into account. A typology of standards for trained teachers is needed so that we can properly see the gaps and elevate this issue to the global level."
    }), "\n", _jsx(_components.p, {
      children: "The provision of ‘child, disability and gender sensitive’ education facilities and ‘safe, non-violent, inclusive and effective learning environments for all’ is a challenge in many countries as this article shows. Meanwhile, measuring progress on the issue is hard because the relevant global indicator is not a single measure, but a set of several dimensions."
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.oecd.org/en/publications/education-at-a-glance-2025_1c0d9c79-en.html",
            children: "Education at a glance – OECD"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://theirworld.org/news/safe-schools-report-reveals-crises-challenges-action-needed-to-educate-children",
            children: "Safe Schools Report – Their World"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://unesdoc.unesco.org/ark:/48223/pf0000232107",
            children: "School-related gender-based violence is preventing the achievement of quality education for all"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
